import * as types from '@dinosband/bv-main-schema/dist/types'
import { isPrivateChatRoom } from '@dinosband/bv-main-schema/dist/chat-room'
import { BvDataCenter, chatRoomQueryOption, MESSAGE_ADDED, SubscriptionManager, _isEqualFilterInfo, filterOutInvalidMessages } from '@dinosband/bv-data-center'
import { CHAT_ROOM_MESSAGES_DISCONTINUED, MessageDiscontinuedEventPayload } from '@dinosband/bv-data-center'
import { Broadcast, ChatRoomLastMessageInfo, Message, MessageType } from '@dinosband/bv-messenger-schema'
import { AttachmentBroadcast, updateUploadingState } from '@dinosband/bv-attachment'
import { queryFirstMessageId } from '@dinosband/bv-messenger-client'
import { _first, _last } from '@dinosband/dbi-utils'
import settings from '@dinosband/bv-settings'

import { apolloClient } from '../../'
import { ChatRoomLastRead } from '../../vuex'
import { ChatRoomStore, ChatRoomContext } from '../../controller-pool'
import { merge } from '../messages'
import { getRootMessage, getMessages } from '../messages/get-messages'
import { updateFilteredMessageCache } from '../filter/message-to-cache'

// import { createChatRoomView, getChatRoomView, updateChatRoomView } from './chat-room-views'
import { ChatRoomDataSubscriber, ChatRoomDataSubscriberDelegate } from './chat-room-data-subscriber'
// import { chatRoomQueryOption } from './utils'
import { MessagesChunkSize } from '.'

const _DEBUG_LOG = process.env.NODE_ENV !== 'production' &&
                   !!process.env.VUE_APP_MESSENGER_DEBUG_LOG


export interface ChatRoomMessageLoadAgent {
  messageDidUpdate: (message: Message) => void
  willReconnect: () => void
  didReconnect: (added?: ChatRoomLastMessageInfo, updated?: ChatRoomLastMessageInfo) => void
}

export class ChatRoomController implements ChatRoomDataSubscriberDelegate {

  dataCenter: BvDataCenter
  context: ChatRoomContext
  chatRoomStore: ChatRoomStore
  chatRoomId: number
  isChannelRoom: boolean
  // messagePreloader?: MessagePreloader
  deviceTokens?: Record<string, unknown> // for unit test of push notification

  private lastReadMessageId?: number
  private excludeDeleted?: boolean
  // private _watchFilter?: () => void
  private _subscriber?: ChatRoomDataSubscriber
  _messagesLoaded = false
  private _loaders: ChatRoomMessageLoadAgent[] = []

  get mainStore () {
    return this.context.mainStore
  }

  constructor (chatRoomId: number, dataCenter: BvDataCenter, context: ChatRoomContext) {
    this.dataCenter = dataCenter
    this.context = context
    this.chatRoomStore = this.context.store
    this.chatRoomId = chatRoomId
    this.isChannelRoom = this.context.isChannelRoom
    this.excludeDeleted = chatRoomQueryOption(this.context.chatRoomType, this.context.isAdmin)?.excludeDeleted
    // this.messagePreloader = new MessagePreloader(this.context) // 앱 초기화 속도를 위해 preloading을 막음
  }

  async init () {
    if (!this.context.isJoined) {
      return
    }
    this.initChatRoomState()
    this.initParticipants()
    this.initSubscriber()
    if (this.context.isPrivateChatroom) {
      await this.initRootMessage()
    }
    // await this.messagePreloader?.load(this.excludeDeleted)
  }

  deinit () {
    this.cleanupSubscriber()
  }

  registerLoader (loader: ChatRoomMessageLoadAgent) {
    if (this._loaders.includes(loader)) {
      return
    }
    this._loaders.push(loader)
  }

  deregisterLoader (loader: ChatRoomMessageLoadAgent) {
    this._loaders = this._loaders.filter((i) => i !== loader)
  }

  started = false

  private initChatRoomState () {
    this.initChatRoomFilter()
    this.initChatRoomMessages()
  }

  private initChatRoomFilter () {
    if (this.chatRoom.type !== types.ChatRoomType.JOB_OFFER &&
        this.chatRoom.type !== types.ChatRoomType.INFO) {
      return
    }
    const info = settings.mesgFilterInfo[this.chatRoomId]
    this.chatRoomStore.filterInfo = info
  }

  private initChatRoomMessages () {
    let messages = this.chatRoomStore.messages
    if (!messages.length) {
      return
    }
    messages = filterOutInvalidMessages(messages, this.excludeDeleted)
    if (messages.length != this.chatRoomStore.messages.length) {
      this.chatRoomStore.messages = messages
    }
  }

  private subscriptions = new SubscriptionManager()
  private mainStoreSubscriptions?: SubscriptionManager
  private initSubscriber () {
    if (!this.context.isJoined) {
      return
    }
    this.subscriptions.init(this.dataCenter.context.pubsub)
    this.subscriptions.subscribe(CHAT_ROOM_MESSAGES_DISCONTINUED, (payload: MessageDiscontinuedEventPayload) => {
      if (payload.chatRoomId !== this.chatRoomId) { return }
      this.onReconnected(payload.added, payload.updated)
    })
    if (this.context.isChannelRoom) {
      this.mainStoreSubscriptions = new SubscriptionManager()
      this.mainStoreSubscriptions.init(this.mainStore.watch)
      this.mainStoreSubscriptions.subscribe(this.mainStore.USER_BLOCKING_CHANGED, (info: unknown) => {
        this.onReconnected(undefined, this.context.lastMessage) // 전체 reload를 위해 updated message에 임의의 message를 사용
      })
    }
    this.subscriptions.subscribe(MESSAGE_ADDED, (i) => {
      if (i.chatRoomId === this.chatRoomId) {
        this.checkRecentMessage(i)
      }
    })
    this._subscriber = new ChatRoomDataSubscriber(this.context, this.dataCenter)
    this._subscriber.delegate = this
  }

  private cleanupSubscriber () {
    this.mainStoreSubscriptions?.cleanup()
    this.mainStoreSubscriptions = undefined
    this.subscriptions.cleanup()
    this._subscriber?.cleanup()
    this._subscriber = undefined
  }

  _objUsers: Set<number> = new Set()

  async start (objId?: number) {
    if (objId) {
      this._objUsers.add(objId)
    }
    if (this.started) {
      return
    }
    this.started = true
    this._subscriber?.start(),
    await Promise.all([
      this.initRootChatRoom(),
      this.initFirstMessageId(),
      this.loadData(),
    ])
  }

  stop (objId?: number) {
    if (objId) {
      this._objUsers.delete(objId)
    }
    if (this._objUsers.size) {
      return
    }
    this._subscriber?.stop()
    this.chatRoomStore.clearMessageSummaries()
    this.chatRoomStore.clearCommentsChatRoomInfos()
    this.trimRecentMessages()
    this.started = false
    // this._messagesLoaded = false
  }

  // prepareWaitMessageSummary () {
  //   if (this.summarySubscription) {
  //     return
  //   }
  //   this.observeMessageSummaries()
  // }

  // cleanupWaitMessageSummary () {
  //   if (this.started || !this.summarySubscription) {
  //     return
  //   }
  //   this.summarySubscription.unsubscribe()
  //   this.summarySubscription = undefined
  // }

  async waitMessageSummary (messageId: number, timeout = 5_000) {
    return await this._subscriber?.waitMessageSummary(messageId, timeout)
  }

  updateRootMessage () {
    this.initRootMessage()
  }

  private get chatRoom (): types.ChatRoom {
    return this.chatRoomStore.state.chatRoom
  }

  private initParticipants () {
    let userProfile: types.Profile | undefined
    if (isPrivateChatRoom(this.chatRoom.type)) {
      const participants = this.chatRoom.participants.map((i) => i.profile)
      this.chatRoomStore.profiles = participants
      this.chatRoomStore.updateUserProfile(this.mainStore.profiles)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const channelId = this.chatRoom.channelId!
      userProfile = this.mainStore.getMyProfileOfChannel(channelId)
      this.chatRoomStore.userProfile = userProfile
      // userProfile = mainStore.profiles.find((i) => i.channels.map((ch) => ch.channelId).includes(channelId))
    }
  }

  private async initRootMessage () {
    if (!this.chatRoom.rootMessageId) {
      return
    }
    const message = await getRootMessage(this.chatRoom.id, this.chatRoom.rootMessageId)
    if (!message) {
      return
    }
    this.chatRoomStore.rootMessage = message
  }

  private async initRootChatRoom () {
    if (!this.chatRoom.rootMessageId) {
      return
    }
    if (!this.chatRoomStore.rootMessage) {
      await this.initRootMessage()
    }
    const rootMessage = this.chatRoomStore.rootMessage
    if (!rootMessage) {
      return
    }
    this.chatRoomStore.rootChatRoom = await this.dataCenter.readChatRoom(rootMessage.chatRoomId)
  }

  private async initFirstMessageId () {
    const result = await queryFirstMessageId(await apolloClient.getClient(), this.chatRoomId, this.context.queryFilter)
    this.chatRoomStore.firstMessageId = result?.id
  }

  async updateUserChatRoomView () {
    if (!this.context.isJoined) {
      return
    }
    const message = this.context.lastMessage
    if (!message || (this.lastReadMessageId && message.id <= this.lastReadMessageId)) {
      return
    }

    this.updateLastRead({ id: message.id, seq: message.seq })
    this.lastReadMessageId = message.id
    const userView = this.context.store.userView
    if (userView) {
      userView.lastMessageId = message.id
      userView.lastMessageSeq = message.seq
      await this.dataCenter.userViews.updateUerView(userView)
    } else {
      if (message.seq == 1 && message.props.type == MessageType.COPIED_JOB_OFFER) {
        return
      }
      await this.dataCenter.userViews.createUserView(this.chatRoomId, message.id, message.seq)
    }
  }

  async updateLastRead (lastRead: ChatRoomLastRead) {
    this.chatRoomStore.lastRead = lastRead
  }

  async didSendMessage (message: Message) {
    // this.addMessage(message)
  }

  private async loadData () {
    const updated = _last(this.chatRoomStore.messages)?.id === this.chatRoomStore.lastMessage?.id
    if (!this._messagesLoaded || !updated || this.context.isFiltered) {
      // await this.tryPreload() // already loaded in constructor
      await this.retrieveRecentMessages()
    }
    // await this._subscriber.retrieve(this.chatRoomStore.messages.map((i) => i.id))
    this._messagesLoaded = true
  }

  async onReconnected (added?: Message, updated?: ChatRoomLastMessageInfo) {
    if (added) {
      this.chatRoomStore.lastMessage = added
    }
    if (!this.started) {
      this.chatRoomStore.messages = []
      this._messagesLoaded = false
      return
    }
    this._loaders.forEach((i) => i.willReconnect())
    if (updated) {
      this.chatRoomStore.messages = []
    }
    this.chatRoomStore.clearMessageSummaries()
    this.chatRoomStore.clearCommentsChatRoomInfos()
    await this.retrieveRecentMessages()
    this._subscriber?.retrieve(this.chatRoomStore.messages.map((i) => i.id))
    this._loaders.forEach((i) => i.didReconnect(added, updated))
  }

  private checkRecentMessage (message: Message) {
    if (this.chatRoom.type !== types.ChatRoomType.COMMENTS) {
      return
    }
    const first = this.chatRoomStore.messages[0]
    if (first && first.id != message.id) {
      return
    }
    if (message.seq == 1) {
      return
    }
    this.retrieveRecentMessages()
  }

  private async retrieveRecentMessages () {
    const needRefresh = this.context.isFiltered
    const exists = this.chatRoomStore.messages
    const first = _first(exists)
    let last = _last(exists)
    if (needRefresh || exists.length < MessagesChunkSize && first && 1 < first.seq) {
      last = undefined
    }
    const queryFilter = this.context.queryFilter
    const useCache = !needRefresh && this.context.isJoined
    const results = await getMessages(this.chatRoomId, 'recent', last, useCache, queryFilter)
    if (!results?.length) {
      if (last == undefined && this.context.isFiltered) {
        this.chatRoomStore.messages = []
        this.chatRoomStore.lastMessage = undefined
        //FIXME: workaround to update messages cursor of message-loader
        this._loaders.forEach((i) => i.messageDidUpdate(undefined as unknown as Message))
      }
      return
    }
    if (needRefresh) {
      this.chatRoomStore.messages = results
    } else {
      const continuos = results.length < MessagesChunkSize
      const merged = merge(exists, results, continuos)
      this.chatRoomStore.messages = merged
    }
    // this._subscriber.retrieve(merged.map((i) => i.id))
    if (this.context.isFiltered) {
      updateFilteredMessageCache(this.chatRoomId, this.chatRoomStore.messages)
    }
  }

  trimRecentMessages () {
    const messageCount = this.chatRoomStore.messages.length
    if (MessagesChunkSize < messageCount) {
      this.chatRoomStore.messages = this.chatRoomStore.messages.slice(-MessagesChunkSize)
    }
  }

  async setFilterInfo (info?: types.MesgFilterInfo) {
    if (_isEqualFilterInfo(this.chatRoomStore.filterInfo, info)) {
      return
    }
    this.chatRoomStore.filterInfo = info
    this.chatRoomStore.messages = []
    if (this.started) {
      await this.retrieveRecentMessages()
      this.initFirstMessageId()
    } else {
      // this.messagePreloader?.load(this.excludeDeleted)
      this._messagesLoaded = false
    }
  }

  ////////////////////////////////////////////////////////////////////////////
  // ChatRoomSubscriberDelegate
  ////////////////////////////////////////////////////////////////////////////

  onBroadcast (broadcast: Broadcast) {
    const attachment = broadcast.props?.attachment as AttachmentBroadcast
    if (attachment) {
      updateUploadingState(attachment.url, attachment.state)
    }
    const tokens = broadcast.tokens
    if (tokens) {
      this.deviceTokens = tokens
    }
  }

}
